import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Icon, Input, Text } from '@node-space/ui-components'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useBankDetailValidation } from 'pages/Contacts/hooks/useBankDetailsValidation'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { AddressTypes, FieldsFetchedFromIbanValidation } from 'pages/Contacts/types/fiatInstruments'
import {
  getIsValidIban,
  setFieldsFetchedFromIbanValidation,
} from 'pages/Contacts/utils/fiatInstruments'
import { BeneficiaryDetailsCurrency, BeneficiaryTransferDestination } from 'types/beneficiaries'
import { AddressDetails } from './AddressDetails'

type InternationalBankDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isBeneficiaryAddress?: boolean
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  formFieldLabels: {
    accountNumber: string
    accountNumberPlaceholder: string
    bankCode: string
    bankCodePlaceholder: string
  }
}

export const InternationalBankDetails = ({
  formValues,
  errors,
  clearErrors,
  register,
  setValue,
  trigger,
  setError,
  formFieldLabels,
}: InternationalBankDetailsProps) => {
  const { t } = useTranslation()

  const { setRequestError } = useMappedErrorMessage()

  const { data: countries, isPending: countriesLoading } = useCountriesQuery(true)
  const [accountNumber, setAccountNumber] = useState<string | undefined>(undefined)
  const [bankCode, setBankCode] = useState<string | undefined>(undefined)
  const [isValidBic, setIsValidBic] = useState<boolean>(false)

  const ibanValidationRef = useRef<string | null>(null)

  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isInternationalBankAccount =
    formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isCountryUs = formValues?.bankCountryCode === 'US'
  const isRoutingNumberVisible = isUsd && isInternationalBankAccount && isCountryUs

  const isIbanCurrency =
    formValues?.currency === BeneficiaryDetailsCurrency.EUR ||
    formValues?.currency === BeneficiaryDetailsCurrency.AED

  const {
    ibanBankDetails,
    validateIban,
    validateBic,
    isValidatingIban,
    isValidatingBic,
    isValidatingIbanError,
  } = useBankDetailValidation({
    intlIban: isIbanCurrency && accountNumber,
    bicSwiftCode: bankCode,
    setRequestError,
  })

  const isBankDetailsValidationLoading = isValidatingIban
  const ibanSupportedPaymentMethods = ibanBankDetails?.supportedPaymentMethods

  const isValidIban = useMemo(() => {
    return getIsValidIban({
      currency: formValues?.currency,
      ibanBankDetails,
      ibanSupportedPaymentMethods,
    })
  }, [formValues?.currency, ibanBankDetails, ibanSupportedPaymentMethods])

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, formValues?.paymentMethod, clearErrors])

  const handleInputChange =
    (field: keyof ContactFiatInstrumentFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, e?.target?.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const handleChange = (field: keyof FieldsFetchedFromIbanValidation, value: string) => {
    setValue(field, value)
  }

  const setIbanBankDetailsData = (details: FieldsFetchedFromIbanValidation) => {
    if (Object.keys(details || {})?.length) {
      Object.keys(details)?.forEach(detail => {
        if (detail === 'bankCountry') {
          handleCountrySelect(details[detail])
        } else {
          handleChange(detail as keyof FieldsFetchedFromIbanValidation, details[detail])
        }
      })
    }
  }

  const handleAccountNumberOnBlur = async () => {
    clearErrors()
    const iban = formValues?.accountNumber

    const isIbanValid = await trigger('accountNumber')
    if (!isIbanValid || !iban) return

    setAccountNumber(iban)

    const ibanAccountDetailsResponse = await validateIban()
    const ibanBankDetails = ibanAccountDetailsResponse?.data

    const hasValidationFailed = ibanBankDetails?.validationFailed?.length >= 1

    const isValidIban = getIsValidIban({
      currency: formValues?.currency,
      ibanBankDetails: ibanBankDetails,
      ibanSupportedPaymentMethods: ibanBankDetails?.supportedPaymentMethods,
    })
    const isInvalidIban = isValidatingIbanError || hasValidationFailed || !isValidIban

    if (isInvalidIban && iban) {
      setError('accountNumber', { message: t('ibanValidationError') })
      return
    }

    if (!hasValidationFailed && isValidIban) {
      const details = setFieldsFetchedFromIbanValidation(ibanBankDetails)
      setIbanBankDetailsData(details)
    }

    ibanValidationRef.current = iban
  }

  const handleInternationalBankCodeOnBlur = async () => {
    if (isValidBic) {
      setIsValidBic(false)
    }
    clearErrors()
    const bic = formValues?.bankCode
    setBankCode(bic)
    const bicValidationResponse = bankCode && (await validateBic())
    if (bicValidationResponse && bicValidationResponse?.data !== 'Valid') {
      setError('bankCode', { message: t('invalidBicCodeErrorMessage') })
    } else {
      setIsValidBic(true)
    }
  }

  const countryOptions = useMemo(() => {
    if (countries?.length) {
      return countries?.map(country => ({
        leftText: country?.name,
        value: country?.code,
      }))
    }

    return []
  }, [countries])

  const handleCountrySelect = (countryCode: string) => {
    const countryName = countryOptions.find(country => country?.value === countryCode)?.leftText
    setValue('bankCountryName', countryName)
    setValue('bankCountryCode', countryCode)
  }

  return (
    !!formValues?.currency && (
      <Box width="full" paddingT={12} gap={12}>
        <Text variant="heading-2">{t('formFields.bankDetails')}</Text>
        <Box paddingY={12}>
          <Input
            {...register('beneficiaryBankName')}
            name="beneficiaryBankName"
            testId="beneficiaryBankName"
            label={t('beneficiarysBankName')}
            placeholder={t('enterBankName')}
            errorText={errors?.beneficiaryBankName?.message}
            error={!!errors?.beneficiaryBankName?.message}
            onChange={handleInputChange('beneficiaryBankName')}
          />
        </Box>

        <Box>
          <Combobox
            {...register('bankCountryCode')}
            label={t('country')}
            id={t('bankCountryCode')}
            selectPlaceholder={t('searchOrSelectACountry')}
            items={countryOptions}
            onChange={handleCountrySelect}
            loading={countriesLoading}
            defaultValue={formValues?.bankCountryCode}
            errorText={errors?.bankCountryCode?.message}
          />
        </Box>

        <Box flex gapX={12} paddingY={12}>
          <Box flexSize="auto" width="full">
            <Input
              {...register('accountNumber')}
              name="accountNumber"
              testId="accountNumber"
              label={formFieldLabels?.accountNumber}
              placeholder={formFieldLabels?.accountNumberPlaceholder}
              errorText={errors?.accountNumber?.message}
              error={!!errors?.accountNumber?.message}
              onBlur={handleAccountNumberOnBlur}
              onChange={handleInputChange('accountNumber')}
              loading={isBankDetailsValidationLoading}
              IconButton={
                isValidIban && <Button variant="ghost" iconElement={<Icon name="Check" />} />
              }
              iconPosition="right"
            />
          </Box>
          <Box flexSize="auto" width="full">
            <Input
              {...register('bankCode')}
              name="bankCode"
              testId="bankCode"
              label={formFieldLabels.bankCode}
              placeholder={formFieldLabels?.bankCodePlaceholder}
              errorText={errors?.bankCode?.message}
              error={!!errors?.bankCode?.message}
              onBlur={handleInternationalBankCodeOnBlur}
              loading={isValidatingBic}
              IconButton={
                isValidBic && <Button variant="ghost" iconElement={<Icon name="Check" />} />
              }
              onChange={handleInputChange('bankCode')}
              iconPosition="right"
            />
          </Box>
        </Box>

        {isRoutingNumberVisible && (
          <Box paddingB={12}>
            <Input
              {...register('routingNumber')}
              testId="routingNumber"
              label={t('formFields.routingNumber')}
              placeholder={t('formFields.placeholder.routingNumber')}
              errorText={errors?.routingNumber?.message}
              error={!!errors?.routingNumber?.message}
              value={formValues?.routingNumber}
              onChange={handleInputChange('routingNumber')}
            />
          </Box>
        )}

        <AddressDetails
          formValues={formValues}
          errors={errors}
          type={AddressTypes.BANK}
          clearErrors={clearErrors}
          register={register}
          setValue={setValue}
        />
      </Box>
    )
  )
}
